import * as React from "react";
import { Button, notification, Card } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  formInitialize,
  formSet,
  formUninitialize,
  useCpFormAnyValueId,
  useCpFormSubmit,
} from "../../gvc/testNewForm/utils";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultServiceAccountInput, inputToServiceAccount } from "../utils";
import { ServiceAccountInput } from "../../../schema/types/serviceAccount/input";
import { ServiceAccount } from "../../../schema/types/serviceAccount/entity";
import { getValidator } from "./validator";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpInputDescription } from "../../gvc/testNewForm/CpInputDescription";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";

interface EmbeddedServiceAccountCreateProps {
  onDone: (itemLink: string, item: ServiceAccount | undefined) => void | Promise<void>;
  onCancel: () => void;
}

export const EmbeddedServiceAccountCreate: React.FC<EmbeddedServiceAccountCreateProps> = ({ onDone, onCancel }) => {
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const input = getDefaultServiceAccountInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  const submitting = useCpFormAnyValueId(formId, "meta.submitting");

  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;

  const submit = useCpFormSubmit({
    id: formId,
    validationFn: validator,
    onSubmit: handleSubmit,
  });

  async function handleSubmit(input: ServiceAccountInput) {
    try {
      const body = inputToServiceAccount(input);
      await request({
        method: "post",
        url: homeLink("serviceaccount"),
        body,
      });
      const itemLink = resourceLink("serviceaccount", body.name);
      notification.success({
        message: "Success",
        description: "Service Account is created",
      });
      let createdServiceAccount: ServiceAccount | undefined;
      try {
        const { data } = await request({ url: itemLink });
        createdServiceAccount = data;
      } catch (e) {}
      onDone(itemLink, createdServiceAccount);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit }}>
      <CpFormValidator validationFn={validator} />
      <div className="flex flex-col gap-4">
        <div className="cpln-form-section cpln-ant-card-container">
          <Card>
            <CardTitle>
              Service Account
              <CpErrorWarningArrayIcon names={["name", "description"]} />
            </CardTitle>
            <CardContent>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name="name">Name</CpLabel>
                  <CpTooltip tooltipProps={{ title: "Cannot be changed after creation (ok, my-item, item1)" }} />
                </CpLabelTooltip>
                <CpInput name="name" style={{ maxWidth: 600 }} />
                <CpErrorWarning name="name" />
              </CpFormItem>
              <CpInputDescription namePath="name" descriptionPath="description" />
            </CardContent>
          </Card>
        </div>
        <CpTagsNew />
        <div className="flex flex-col gap-2">
          <Button type="primary" danger onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {!IS_DEPLOYMENT_ENV_PROD && (
            <Button type="default" onClick={() => formSet(formId, "meta.debugging", true)}>
              Debug
            </Button>
          )}
          <Button type="primary" className="action" onClick={submit} loading={submitting} disabled={submitting}>
            Create
          </Button>
        </div>
      </div>
      <CpDebugModal inputToItemFn={inputToServiceAccount} kind="serviceaccount" namePath="name" title="Service Account" />
    </CpFormContext.Provider>
  );
};
